import Table from '../../../../utils/ResultViewer/Table'
import axios from 'axios'
import { AdvanceSettingsModal } from '../../components/AdvanceSettingsModal'
import React from 'react'

class DataExportTable extends Table {
  private pollTimer: any
  state: any = {
    ...this.state,
    showAdvance: false,
    advanceData: null as any,
  }

  componentDidMount(): void {
    // Call parent behavior and then start polling
    // @ts-ignore
    super.componentDidMount()
    this.startPolling()
  }

  componentWillUnmount(): void {
    this.stopPolling()
  }

  private startPolling() {
    this.stopPolling()
    this.pollTimer = window.setInterval(() => {
      if (document.hidden) return
      if (this.shouldPollStatuses()) {
        this.fetchData()
      }
    }, 5000)
  }

  private stopPolling() {
    if (this.pollTimer) {
      clearInterval(this.pollTimer)
      this.pollTimer = null
    }
  }

  private shouldPollStatuses(): boolean {
    const rows: any[] = ((this.state as any)?.data?.data as any[]) || []
    return rows.some((row: any) => ['queued', 'preparing'].includes(row['status']))
  }

  private openAdvance(data: any) {
    this.setState({ showAdvance: true, advanceData: data })
  }

  public closeAdvance = () => {
    this.setState({ showAdvance: false, advanceData: null })
  }
  private async downloadExport(dataExportId: string) {
    try {
      const response = await axios.get(`/data_export/download/${dataExportId}`, {
        responseType: 'blob',
      })
      const contentType = (response.headers as any)['content-type'] || 'application/octet-stream'
      const disposition = (response.headers as any)['content-disposition'] || ''
      let filename = 'data-export'
      const match = /filename="?([^";]+)"?/i.exec(disposition)
      if (match && match[1]) {
        filename = match[1]
      }
      const blob = new Blob([response.data], { type: contentType })
      const url = window.URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      link.download = filename
      document.body.appendChild(link)
      link.click()
      link.remove()
      window.URL.revokeObjectURL(url)
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error('Download failed', e)
    }
  }

  private async runExport(dataExportId: string) {
    try {
      await axios.post(`/data_export/run/${dataExportId}`)
      this.fetchData()
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error('Run failed', e)
    }
  }

  private async cancelExport(dataExportId: string) {
    try {
      await axios.post(`/data_export/cancel/${dataExportId}`)
      this.fetchData()
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error('Cancel failed', e)
    }
  }

  renderRow(row: any, key: string, colIndex: number): JSX.Element {
    if (key === 'edit_actions') {
      return (
        <td key={colIndex}>
          <div className='d-flex justify-content-end flex-shrink-0'>
            <button
              onClick={() => this.props.editCallback(row[key], this.fetchData)}
              className='btn btn-icon btn-bg-light btn-active-color-primary btn-sm me-1'
              title={row['status'] === 'completed' ? 'View' : 'Edit'}
            >
              <span className={row['status'] === 'completed' ? 'fa fa-eye' : 'fa fa-edit'}></span>
            </button>
            {['queued', 'preparing'].includes(row['status']) ? (
              <button
                onClick={() => this.cancelExport(row[key])}
                className='btn btn-icon btn-bg-light btn-active-color-info btn-sm me-1'
                title='Abort'
              >
                <span className='fa fa-times'></span>
              </button>
            ) : row['status'] === 'completed' ? (
              <button
                onClick={() => this.downloadExport(row[key])}
                className='btn btn-icon btn-bg-light btn-active-color-success btn-sm me-1'
                title='Download'
              >
                <span className='fa fa-file-download'></span>
              </button>
            ) : (
              <button
                onClick={() => this.runExport(row[key])}
                className='btn btn-icon btn-bg-light btn-active-color-success btn-sm me-1'
                title='Run'
                disabled={!['created', 'failed', 'cancelled'].includes(row['status'])}
              >
                <span className='fa fa-play'></span>
              </button>
            )}
            <button
              onClick={() => this.props.deleteCallback(row[key], this.fetchData)}
              className='btn btn-icon btn-bg-light btn-active-color-danger btn-sm me-1'
            >
              <span className='fa fa-trash'></span>
            </button>
          </div>
        </td>
      )
    } else if (key === 'advance_settings') {
      return (
        <td key={colIndex}>
          <button
            onClick={() => this.openAdvance(row[key])}
            className='btn btn-icon btn-bg-light btn-active-color-primary btn-sm'
            title='View settings'
          >
            <span className='fa fa-eye'></span>
          </button>
        </td>
      )
    } else if (key === 'status') {
      if (row[key] === 'created') {
        // created, queued, preparing, completed, failed
        return (
          <td key={colIndex}>
            <div className='badge badge-primary'>{row[key]}</div>
          </td>
        )
      } else if (row[key] === 'queued') {
        return (
          <td key={colIndex}>
            <div className='badge badge-warning'>{row[key]}</div>
          </td>
        )
      } else if (row[key] === 'preparing') {
        return (
          <td key={colIndex}>
            <div className='badge badge-info'>{row[key]}</div>
          </td>
        )
      } else if (row[key] === 'completed') {
        return (
          <td key={colIndex}>
            <div className='badge badge-success'>{row[key]}</div>
          </td>
        )
      } else if (row[key] === 'failed') {
        return (
          <td key={colIndex}>
            <div className='badge badge-danger'>{row[key]}</div>
          </td>
        )
      } else {
        return (
          <td key={colIndex}>
            <div className='badge badge-secondary'>{row[key]}</div>
          </td>
        )
      }
    }
    return super.renderRow(row, key, colIndex)
  }
}

class DataExportTableWithModal extends DataExportTable {
  render(): JSX.Element {
    const base = super.render()
    return (
      <>
        {base}
        <AdvanceSettingsModal
          show={(this.state as any).showAdvance}
          onHide={this.closeAdvance}
          data={(this.state as any).advanceData}
        />
      </>
    )
  }
}

export default DataExportTableWithModal
